import { TableCell } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { productPerformanceColumns, walletFinancialPositionsMessages, walletPagePerformanceSectionMessages } from 'azshared';
import React, { Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Loader, LoaderSize } from '../../../../../components/loader/loader';
import { AppTableCell } from '../../../../../components/table/table-cell';
import { AppTableCellHeader } from '../../../../../components/table/table-cell-header';
import './performance-table.scss';

interface PerformanceTableProps {
  performanceList: any;
  isLoading: boolean;
}

export const PerformanceTable = (props: PerformanceTableProps) => {
  const { performanceList, isLoading } = props;

  const { formatMessage } = useIntl();
  const [flagShowDisclaimerSGR, setFlagShowDisclaimerSGR] = useState(false);
  const [flagShowDisclaimerFIA, setFlagShowDisclaimerFIA] = useState(false);
  const [flagShowDisclaimerMergingFund, setFlagShowDisclaimerMergingFund] = useState(false);
  const columnsToHide = ['gain', 'averageDeposits', 'plusMinus'];

  useEffect(() => {
    const hasSGRFlag = performanceList?.products?.some((product) => product.flagSGR) || false;
    const hasMergingFund = performanceList?.products?.some((product) => product.isMergingFund) || false;
    const hasFIAFlag = performanceList?.products?.some((product) => product.flagFiaMultirichiamo) || false;
    setFlagShowDisclaimerSGR(hasSGRFlag);
    setFlagShowDisclaimerFIA(hasFIAFlag);
    setFlagShowDisclaimerMergingFund(hasMergingFund);
  }, [performanceList]);
  return (
    <Fragment>
      <TableContainer className='performance-table'>
        <Table aria-label='table'>
          <TableHead>
            <TableRow>
              {productPerformanceColumns.map((column, index) => (
                <AppTableCellHeader key={index} className='performance-table-header'>
                  {formatMessage(column.headerLabel)}
                </AppTableCellHeader>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={productPerformanceColumns.length + 1}>
                  <div className='table-loader'>
                    <Loader loadersize={LoaderSize.XL} />
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {!isLoading && performanceList?.products.length > 0 ? (
            <>
              <TableBody>
                {performanceList?.products.map((product, index) => (
                  <TableRow key={index} className='performance-table-row'>
                    {productPerformanceColumns.map((col) => {
                      return (
                        <Fragment key={col.key}>
                          {col.key === 'description' && (
                            <AppTableCell key={col.key} align='left' type={col.type} className='performance-table-cell-description'>
                              <p>
                                {product[col.key]} {(product.flagSGR || product.isMergingFund) && '*'} {product.flagFiaMultirichiamo && '***'}
                              </p>
                            </AppTableCell>
                          )}
                          {col.key !== 'description' && (
                            <AppTableCell
                              key={col.key}
                              showNd={true}
                              align='left'
                              type={columnsToHide.includes(col.key) && product.flagFiaMultirichiamo ? 'string' : col.type}>
                              {columnsToHide.includes(col.key) && product.flagFiaMultirichiamo
                                ? formatMessage(walletPagePerformanceSectionMessages.notAvialableLabel)
                                : product[col.key]}
                            </AppTableCell>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow className='performance-table-row-footer'>
                  {productPerformanceColumns.map((col) => (
                    <Fragment key={col.key}>
                      {col.key === 'description' && (
                        <AppTableCell key={col.key} align='left' type='string' className='performance-table-footer-total'>
                          {formatMessage(walletPagePerformanceSectionMessages.footerTotalLabel)}
                        </AppTableCell>
                      )}
                      {col.key === 'amount' && (
                        <AppTableCell showNd={true} key={col.key} align='left' type='amount' className='performance-table-footer-total'>
                          {performanceList?.totalEquivalent}
                        </AppTableCell>
                      )}
                      {col.key === 'gain' && (
                        <AppTableCell showNd={true} key={col.key} align='left' type='percentage' className='performance-table-footer-total'>
                          {performanceList?.totalGain}
                        </AppTableCell>
                      )}
                      {(col.key === 'contract' || col.key === 'type') && (
                        <AppTableCell key={col.key} align='left' type='string' className='performance-table-footer-total'>
                          {''}
                        </AppTableCell>
                      )}

                      {col.key !== 'description' && col.key !== 'contract' && col.key !== 'type' && col.key !== 'amount' && col.key !== 'gain' && (
                        <AppTableCell key={col.key} align='left' type='string' className='performance-table-footer-total'>
                          {''}
                        </AppTableCell>
                      )}
                    </Fragment>
                  ))}
                </TableRow>
              </TableFooter>
            </>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={productPerformanceColumns.length + 1}>
                  <div className='table-no-results'>
                    <p>{formatMessage(walletPagePerformanceSectionMessages.noProducts)}</p>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && (
        <div className='performance-table-disclaimer'>
          {flagShowDisclaimerSGR && <p className='performance-table-disclaimer-text'>{formatMessage(walletPagePerformanceSectionMessages.bottomDisclaimer)}</p>}
          <p className='performance-table-disclaimer-text'>{formatMessage(walletPagePerformanceSectionMessages.disclaimer)}</p>
          {flagShowDisclaimerFIA && <p className='performance-table-disclaimer-text'>{formatMessage(walletPagePerformanceSectionMessages.disclaimerFia)}</p>}
          {flagShowDisclaimerMergingFund && (
            <p className='performance-table-disclaimer-text'>{formatMessage(walletFinancialPositionsMessages.mergedFundsFooterDisclaimer)}</p>
          )}
        </div>
      )}
    </Fragment>
  );
};
